import { useEffect, useRef } from "react";
import "./HeroBulge.css";

// Cores da marca (design-system.css) em RGB, para variar a opacidade no canvas.
const LILAC = "201, 203, 240"; // --cz-on-dark-2
const PINK = "232, 51, 90"; // --cz-pink
const PINK_SOFT = "255, 122, 150"; // --cz-pink-soft
const ORB_LILAC = "140, 134, 232"; // início de --cz-grad-orb
const ORB_PINK = "194, 86, 126"; // fim de --cz-grad-orb
const SHADE = "8, 8, 40";
const NAVY = "21, 22, 92"; // --cz-navy

// Paleta por fundo. `line`/`dot` são as opacidades longe da bolha e no centro dela; `blend` é como o volume da
// bolha se mistura ao fundo ("screen" clareia o navy; no fundo claro o volume é pintado por cima).
const TONES = {
  dark: { base: LILAC, peak: PINK_SOFT, line: [0.07, 0.45], dot: [0.2, 0.9], accent: 0.8, blend: "screen", shade: 0.35, lilac: 0.4, pink: 0.28, white: 0.16 },
  light: { base: NAVY, peak: PINK, line: [0.07, 0.3], dot: [0.16, 0.7], accent: 0.55, blend: "source-over", shade: 0.05, lilac: 0.16, pink: 0.12, white: 0.55 },
} as const;

const GRID_PUSH = 0.8; // empurrão da grade para fora do centro da bolha (no centro ela fica 1 + GRID_PUSH vezes maior)
const STEP = 8; // de quantos em quantos px as linhas da grade são amostradas, para poderem curvar
const DOT = 1.1; // raio dos pontos nos cruzamentos da grade
const FOLLOW = 0.05; // segundos para a bolha alcançar o cursor (maior = mais arrastada atrás dele)
const GROW = 0.15; // segundos para a bolha inflar ao entrar...
const SHRINK = 0.35; // ...e para murchar ao sair
const DAMPING = 0.62; // abaixo de 1 cada peça dá um leve quique ao assentar

// Como cada tipo de peça reage: `push` empurra para fora do centro da bolha, `scale` aumenta (a peça "sai da
// tela"), `tilt` é a inclinação máxima em graus e `stiffness` a rigidez da mola.
type Kind = "char" | "item" | "shape";
const KINDS: Record<Kind, { push: number; scale: number; tilt: number; stiffness: number }> = {
  char: { push: 0.35, scale: 0.4, tilt: 18, stiffness: 190 },
  // botões: push = scale faz o zoom acontecer em volta do cursor, então o botão não foge do clique
  item: { push: 0.14, scale: 0.14, tilt: 8, stiffness: 140 },
  shape: { push: GRID_PUSH, scale: 0.25, tilt: 0, stiffness: 110 },
};

type Body = {
  el: HTMLElement;
  kind: Kind;
  x: number; // centro em repouso, relativo à seção
  y: number;
  k: number; // rigidez da mola, um pouco diferente em cada peça para cada uma reagir no seu tempo
  dx: number;
  dy: number;
  lift: number; // 0 a 1: quanto da bolha está embaixo da peça
  vx: number;
  vy: number;
  vl: number;
  z: number;
  still: boolean;
};

// pseudoaleatório estável em [0, 1)
const hash = (a: number, b: number) => {
  const n = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return n - Math.floor(n);
};

// posição na página ignorando transforms (que mudam a cada quadro)
const pageOffset = (el: HTMLElement) => {
  let x = 0;
  let y = 0;
  for (let node: HTMLElement | null = el; node; node = node.offsetParent as HTMLElement | null) {
    x += node.offsetLeft;
    y += node.offsetTop;
  }
  return { x, y };
};

const clearBody = (b: Body) => {
  b.el.style.transform = "";
  b.el.style.zIndex = "";
  b.el.style.removeProperty("--lift");
  b.z = 0;
};

// Fundo do hero: uma grade que "incha" como uma bolha embaixo do cursor e vai atrás dele. Elementos da seção
// marcados com data-bulge="shape" (formas decorativas) reagem à mesma bolha; data-bulge="char" (letras) e os
// filhos de [data-bulge-items] (botões) também, se algum dia forem usados.
// Vai como primeiro filho de uma seção com position: relative, com o conteúdo dela acima (z-index 1).
// `size` escala o raio da bolha (1 = padrão). O empurrão cresce na mesma proporção, para a bolha menor deformar
// com a mesma intensidade (até 2x, acima disso a grade dobra sobre si mesma).
export default function HeroBulge({ tone = "dark", size = 1 }: { tone?: keyof typeof TONES; size?: number }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const surface = rootRef.current?.parentElement;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!surface || !canvas || !ctx) return;
    const pal = TONES[tone];
    const boost = Math.min(1 / size, 2);
    const gridPush = GRID_PUSH * boost;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const pointer = { x: 0, y: 0, active: false };
    const bubble = { x: 0, y: 0, strength: 0 };
    const drawn = { x: 0, y: 0, strength: -1 };
    let bodies: Body[] = [];
    let needsMeasure = true;
    let w = 0;
    let h = 0;
    let dpr = 1;
    let gap = 56;
    let radius = 130;
    let raf = 0;
    let last = 0;

    const measure = () => {
      needsMeasure = false;
      const origin = pageOffset(surface);
      const prev = new Map(bodies.map((b) => [b.el, b] as const));
      const next: Body[] = [];
      surface.querySelectorAll<HTMLElement>("[data-bulge], [data-bulge-items] > *").forEach((el, i) => {
        if (!el.offsetParent) return; // escondido (display: none)
        const tag = el.dataset.bulge;
        const kind: Kind = tag === "char" || tag === "shape" ? tag : "item";
        const body: Body = prev.get(el) ?? {
          el,
          kind,
          x: 0,
          y: 0,
          k: KINDS[kind].stiffness * (0.8 + 0.4 * hash(i, 1)),
          dx: 0,
          dy: 0,
          lift: 0,
          vx: 0,
          vy: 0,
          vl: 0,
          z: 0,
          still: true,
        };
        prev.delete(el);
        const pos = pageOffset(el);
        body.x = pos.x - origin.x + el.offsetWidth / 2;
        body.y = pos.y - origin.y + el.offsetHeight / 2;
        next.push(body);
      });
      prev.forEach(clearBody); // saíram da página (troca de idioma, por exemplo)
      bodies = next;
    };

    const draw = () => {
      const { x: bx, y: by, strength: s } = bubble;
      drawn.x = bx;
      drawn.y = by;
      drawn.strength = s;
      const r = radius;
      const inv = 1 / (2 * r * r);
      let wx = 0;
      let wy = 0;
      // leva um ponto da grade para onde a bolha o empurrou e devolve a força da bolha ali (0 a 1)
      const warp = (x: number, y: number) => {
        const ex = x - bx;
        const ey = y - by;
        const f = s * Math.exp(-(ex * ex + ey * ey) * inv);
        wx = x + ex * f * gridPush;
        wy = y + ey * f * gridPush;
        return f;
      };
      // cor-base discreta longe da bolha, mais forte e puxada para o rosa no centro dela
      const tint = ([base, peak]: readonly number[]) => {
        if (!s) return `rgba(${pal.base}, ${base})`;
        const g = ctx.createRadialGradient(bx, by, 0, bx, by, r * 3);
        g.addColorStop(0, `rgba(${pal.peak}, ${base + (peak - base) * s})`);
        g.addColorStop(0.35, `rgba(${pal.base}, ${base + (peak - base) * 0.5 * s})`);
        g.addColorStop(1, `rgba(${pal.base}, ${base})`);
        return g;
      };
      const glow = (x: number, y: number, rad: number, rgb: string, alpha: number) => {
        const g = ctx.createRadialGradient(x, y, 0, x, y, rad);
        g.addColorStop(0, `rgba(${rgb}, ${alpha})`);
        g.addColorStop(1, `rgba(${rgb}, 0)`);
        ctx.fillStyle = g;
        ctx.fillRect(x - rad, y - rad, rad * 2, rad * 2);
      };

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const ox = ((w / 2) % gap) + 0.5;
      const oy = ((h / 2) % gap) + 0.5;
      ctx.beginPath();
      for (let x = ox; x < w; x += gap) {
        warp(x, 0);
        ctx.moveTo(wx, wy);
        for (let y = STEP; y < h + STEP; y += STEP) {
          warp(x, y);
          ctx.lineTo(wx, wy);
        }
      }
      for (let y = oy; y < h; y += gap) {
        warp(0, y);
        ctx.moveTo(wx, wy);
        for (let x = STEP; x < w + STEP; x += STEP) {
          warp(x, y);
          ctx.lineTo(wx, wy);
        }
      }
      ctx.lineWidth = 1;
      ctx.strokeStyle = tint(pal.line);
      ctx.stroke();

      // pontos nos cruzamentos, alguns em rosa; perto da bolha crescem, como sob uma lente
      const accents: number[] = [];
      ctx.beginPath();
      for (let i = 0; ox + i * gap < w; i++) {
        for (let j = 0; oy + j * gap < h; j++) {
          const rad = DOT * (1 + 1.5 * warp(ox + i * gap, oy + j * gap));
          if (hash(i, j) < 0.05) {
            accents.push(wx, wy, rad * 1.8);
          } else {
            ctx.moveTo(wx + rad, wy);
            ctx.arc(wx, wy, rad, 0, Math.PI * 2);
          }
        }
      }
      ctx.fillStyle = tint(pal.dot);
      ctx.fill();
      ctx.beginPath();
      for (let n = 0; n < accents.length; n += 3) {
        ctx.moveTo(accents[n] + accents[n + 2], accents[n + 1]);
        ctx.arc(accents[n], accents[n + 1], accents[n + 2], 0, Math.PI * 2);
      }
      ctx.fillStyle = `rgba(${PINK}, ${pal.accent})`;
      ctx.fill();

      if (!s) return;
      // volume da bolha nas cores do orb: luz lilás com brilho branco, sombra embaixo à direita com reflexo rosa.
      // O brilho branco (a parte mais clara) fica exatamente embaixo do cursor; o resto se posiciona a partir dele.
      glow(bx + r * 0.9, by + r, r * 1.3, SHADE, pal.shade * s);
      ctx.globalCompositeOperation = pal.blend;
      glow(bx + r * 0.1, by + r * 0.1, r * 1.5, ORB_LILAC, pal.lilac * s);
      glow(bx + r * 0.85, by + r * 0.95, r * 0.9, ORB_PINK, pal.pink * s);
      glow(bx, by, r * 0.4, "255, 255, 255", pal.white * s);
      ctx.globalCompositeOperation = "source-over";
    };

    const place = (b: Body) => {
      if (Math.abs(b.dx) + Math.abs(b.dy) < 0.05 && Math.abs(b.lift) < 0.001) {
        clearBody(b);
        return;
      }
      const { scale, tilt } = KINDS[b.kind];
      const push = KINDS[b.kind].push * boost;
      // graus por px de deslocamento (o deslocamento máximo, push * 0.61 * raio, acontece a 1 raio do centro)
      const deg = tilt / (push * 0.61 * radius);
      b.el.style.transform =
        `perspective(500px) translate3d(${b.dx.toFixed(2)}px, ${b.dy.toFixed(2)}px, 0) ` +
        `rotateX(${(-b.dy * deg).toFixed(2)}deg) rotateY(${(b.dx * deg).toFixed(2)}deg) ` +
        `scale(${(1 + b.lift * scale).toFixed(4)})`;
      if (b.kind !== "char") return;
      b.el.style.setProperty("--lift", b.lift.toFixed(3));
      // a letra mais alta fica por cima das vizinhas
      const z = b.lift > 0.02 ? 1 + Math.round(b.lift * 20) : 0;
      if (z !== b.z) {
        b.el.style.zIndex = z ? String(z) : "";
        b.z = z;
      }
    };

    const tick = (now: number) => {
      raf = 0;
      const dt = last ? Math.min(Math.max((now - last) / 1000, 0), 1 / 30) : 1 / 60;
      last = now;
      if (needsMeasure) measure();

      const rect = surface.getBoundingClientRect();
      const px = pointer.x - rect.left;
      const py = pointer.y - rect.top;
      // a rolagem pode tirar a seção de baixo do cursor sem disparar pointerleave
      const inside = pointer.active && px >= 0 && py >= 0 && px <= rect.width && py <= rect.height;
      let busy = false;

      if (inside) {
        if (bubble.strength < 0.02) {
          // nasce já embaixo do cursor, em vez de deslizar de onde sumiu
          bubble.x = px;
          bubble.y = py;
        }
        const follow = 1 - Math.exp(-dt / FOLLOW);
        bubble.x += (px - bubble.x) * follow;
        bubble.y += (py - bubble.y) * follow;
        busy = Math.abs(px - bubble.x) + Math.abs(py - bubble.y) > 0.1;
      }
      const goal = inside ? 1 : 0;
      bubble.strength += (goal - bubble.strength) * (1 - Math.exp(-dt / (inside ? GROW : SHRINK)));
      if (Math.abs(goal - bubble.strength) < 0.002) bubble.strength = goal;
      else busy = true;

      const inv = 1 / (2 * radius * radius);
      bodies.forEach((b) => {
        const ex = b.x - bubble.x;
        const ey = b.y - bubble.y;
        const lift = bubble.strength * Math.exp(-(ex * ex + ey * ey) * inv);
        const tx = ex * lift * KINDS[b.kind].push * boost;
        const ty = ey * lift * KINDS[b.kind].push * boost;
        // mola amortecida: cada peça persegue o alvo no seu próprio tempo
        const c = 2 * DAMPING * Math.sqrt(b.k);
        b.vx += (b.k * (tx - b.dx) - c * b.vx) * dt;
        b.vy += (b.k * (ty - b.dy) - c * b.vy) * dt;
        b.vl += (b.k * (lift - b.lift) - c * b.vl) * dt;
        b.dx += b.vx * dt;
        b.dy += b.vy * dt;
        b.lift += b.vl * dt;

        const still =
          Math.abs(tx - b.dx) + Math.abs(ty - b.dy) + Math.abs(b.vx) + Math.abs(b.vy) < 0.05 &&
          Math.abs(lift - b.lift) + Math.abs(b.vl) < 0.001;
        if (still) {
          b.dx = tx;
          b.dy = ty;
          b.lift = lift;
          b.vx = 0;
          b.vy = 0;
          b.vl = 0;
          if (b.still) return;
        } else {
          busy = true;
        }
        b.still = still;
        place(b);
      });

      if (bubble.x !== drawn.x || bubble.y !== drawn.y || bubble.strength !== drawn.strength) draw();
      if (busy) raf = requestAnimationFrame(tick);
      else last = 0;
    };

    const start = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = surface.clientWidth;
      h = surface.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      gap = w < 600 ? 40 : 56;
      radius = Math.min(140, Math.max(70, w * 0.09)) * size;
      needsMeasure = true;
      draw();
    };

    const ro = new ResizeObserver(resize);
    ro.observe(surface);
    if (reduced) return () => ro.disconnect();

    let alive = true;
    document.fonts.ready.then(() => {
      if (alive) needsMeasure = true; // a Poppins muda a largura das letras ao carregar
    });

    const onMove = (e: PointerEvent) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.active = true;
      start();
    };
    const onEnter = (e: PointerEvent) => {
      needsMeasure = true;
      onMove(e);
    };
    const onLeave = () => {
      pointer.active = false;
      start();
    };
    // no toque não existe "sair": a bolha murcha quando o dedo levanta
    const onUp = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") onLeave();
    };
    const onScroll = () => {
      if (pointer.active) start();
    };

    surface.addEventListener("pointerenter", onEnter);
    surface.addEventListener("pointermove", onMove);
    surface.addEventListener("pointerdown", onMove);
    surface.addEventListener("pointerup", onUp);
    surface.addEventListener("pointerleave", onLeave);
    surface.addEventListener("pointercancel", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      surface.removeEventListener("pointerenter", onEnter);
      surface.removeEventListener("pointermove", onMove);
      surface.removeEventListener("pointerdown", onMove);
      surface.removeEventListener("pointerup", onUp);
      surface.removeEventListener("pointerleave", onLeave);
      surface.removeEventListener("pointercancel", onLeave);
      window.removeEventListener("scroll", onScroll);
      bodies.forEach(clearBody);
    };
  }, [tone, size]);

  return (
    <div ref={rootRef} className="cz-bulge" aria-hidden="true">
      <canvas ref={canvasRef} className="cz-bulge__canvas" />
    </div>
  );
}
